import { useCallback, useEffect, useRef } from 'react';
import { NOTICE_CHANGED, NOTICE_STORAGE_KEY } from '../services/systemNoticeEvents';

// One bounded request per reader. Focus/reconnect and local writes bypass the
// five-second foreground fallback; old account responses are always cancelled.
export default function useLiveNoticeRefresh(read, enabled = true) {
  const trigger = useRef(() => Promise.resolve());
  useEffect(() => {
    if (!enabled) { trigger.current = () => Promise.resolve(); return; }
    let stopped = false, current = null;
    const run = (replace = false) => {
      if (stopped) return Promise.resolve();
      if (current && !replace) return current.promise;
      current?.controller.abort();
      const request = { controller: new AbortController() }; current = request;
      request.promise = Promise.resolve().then(() => read(request.controller.signal)).catch(() => {
        // Readers retain their last confirmed state on transient failures.
      }).finally(() => { if (current === request) current = null; });
      return request.promise;
    };
    const refresh = () => run(true);
    const resume = () => { if (document.visibilityState !== 'hidden') refresh(); };
    const storage = event => { if (event.key === NOTICE_STORAGE_KEY) refresh(); };
    trigger.current = refresh;
    run();
    const timer = setInterval(() => { if (document.visibilityState !== 'hidden') run(); }, 5000);
    window.addEventListener('focus', resume); window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', resume);
    window.addEventListener(NOTICE_CHANGED, refresh); window.addEventListener('storage', storage);
    return () => {
      stopped = true; current?.controller.abort(); clearInterval(timer);
      window.removeEventListener('focus', resume); window.removeEventListener('online', resume);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener(NOTICE_CHANGED, refresh); window.removeEventListener('storage', storage);
      trigger.current = () => Promise.resolve();
    };
  }, [read, enabled]);
  return useCallback(() => trigger.current(), []);
}
